import { ArrowLeftIcon, CheckCircleIcon, CircleXIcon, LockOpenIcon, PlusCircleIcon } from 'lucide-react'
import React, { ReactEventHandler, useEffect, useState } from 'react'
import { passwordStrength } from 'check-password-strength'
import { useNavigate, useParams } from 'react-router-dom'
import { Form } from 'radix-ui'
import {
  AlertDialog,
  Button,
  Callout,
  Card,
  Container,
  Flex,
  Grid,
  Heading,
  Progress,
  ProgressProps,
  Spinner,
  Strong,
  Text,
  TextField,
} from '@radix-ui/themes'

import useUserActivateFirst, { PasswordActions } from '../hooks/useUserActivateFirst'

import useTokenFetcher from '@/hooks/useTokenFetcher'
import { AppPath } from '@/enums/AppPath'
import Token from '@/interfaces/Token'

interface FormFields {
  confirmPassword: string
  password: string
  email: string
}

const base64UrlDecode = (base64UrlString: string): string => {
  let base64 = base64UrlString.replace(/-/g, '+').replace(/_/g, '/')

  const padding = base64.length % 4

  if (padding) {
    base64 += '='.repeat(4 - padding)
  }

  const decodedString = atob(base64)

  return decodeURIComponent(
    Array.from(decodedString, (char) => '%' + ('00' + char.charCodeAt(0).toString(16)).slice(-2)).join('')
  )
}

const UserActivate: React.FC = () => {
  const { fetchToken, errorFetchToken, loading: isFetchingToken } = useTokenFetcher()
  const { userActivateFirst, errorUserActivateFirst } = useUserActivateFirst()
  const [action, setAction] = useState<PasswordActions>()
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [token, setToken] = useState<Token | null>(null)
  const [isLoading, setIsLoading] = useState(true)
  const [success, setSuccess] = useState(false)
  const [strength, setStrength] = useState(0)
  const { id } = useParams<{ id: string }>()
  const navigate = useNavigate()

  const [formData, setFormData] = useState<FormFields>({
    confirmPassword: '',
    password: '',
    email: '',
  })

  useEffect(() => {
    if (!id) {
      return
    }

    const fetchDataAsync = async () => {
      const data = await fetchToken(id)

      setIsLoading(false)

      if (!data) {
        return
      }

      setToken(data)

      const decoded = base64UrlDecode(id)
      const lastChar: string = decoded[decoded.length - 1]

      setAction(lastChar === '0' ? PasswordActions.Activate : PasswordActions.Change)
    }

    void fetchDataAsync()
  }, [id, fetchToken])

  const goToSignIn = () => navigate(`/${AppPath.Users}/sign-in`)

  if (isLoading || isFetchingToken) {
    return (
      <Container p="4" size="2">
        <Callout.Root color="gray">
          <Callout.Icon>
            <Spinner size="1" />
          </Callout.Icon>
          <Callout.Text>Verificando token...</Callout.Text>
        </Callout.Root>
      </Container>
    )
  }

  if (!token) {
    return (
      <AlertDialog.Root open>
        <AlertDialog.Content>
          <AlertDialog.Title>El token no es válido o ha expirado</AlertDialog.Title>
          <AlertDialog.Description size="2">
            <Text weight="bold">Puedes intentar:</Text>
            <ul>
              <li>Verificar que el enlace no haya sido modificado.</li>
              <li>Solicitar un nuevo enlace de activación.</li>
              <li>Contactar al soporte técnico si el problema persiste.</li>
            </ul>
          </AlertDialog.Description>

          <Flex gap="3" mt="4">
            <AlertDialog.Action>
              <Button onClick={goToSignIn}>
                <ArrowLeftIcon />
                <Text>Volver</Text>
              </Button>
            </AlertDialog.Action>
          </Flex>
        </AlertDialog.Content>
      </AlertDialog.Root>
    )
  }

  if (success) {
    return (
      <AlertDialog.Root open>
        <AlertDialog.Content>
          <AlertDialog.Title>¡Listo!</AlertDialog.Title>
          <AlertDialog.Description size="2">
            Tu nueva contraseña está activa. Ya puedes iniciar sesión.
          </AlertDialog.Description>

          <Flex gap="3" mt="4">
            <AlertDialog.Action>
              <Button onClick={goToSignIn}>
                <ArrowLeftIcon />
                <Text>Volver al Ingreso</Text>
              </Button>
            </AlertDialog.Action>
          </Flex>
        </AlertDialog.Content>
      </AlertDialog.Root>
    )
  }

  const handleInputChange = ({ target: { name, value } }: React.ChangeEvent<HTMLInputElement>) => {
    if (name === 'password') {
      const { id } = passwordStrength(value)

      setStrength(id ?? 0)
    }

    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }))
  }

  const handleSubmit: ReactEventHandler<HTMLFormElement> = async (e) => {
    e.preventDefault()

    setIsSubmitting(true)

    const matches = formData.password === formData.confirmPassword

    if (!matches || !action || !id || strength < 2) {
      setIsSubmitting(false)

      return
    }

    const res = await userActivateFirst({
      password: formData.password,
      email: formData.email,
      value: id,
      action,
    })

    if (!res) {
      setIsSubmitting(false)

      return
    }

    setSuccess(true)
  }

  const checkMatches = (value: string, formData: FormData): boolean => {
    if (!value || !formData) {
      return false
    }

    const password = formData.get('password')

    if (!password) {
      return false
    }

    return value !== password
  }

  const strengthColor: ProgressProps['color'][] = ['gray', 'red', 'orange', 'green']

  return (
    <Container p="4">
      <Grid columns={{ sm: '1', md: '2' }} gap="4">
        <Flex gap="2" direction="column">
          <Heading>¡Hola!</Heading>
          <Text>
            Para {action === PasswordActions.Activate ? 'activar tu cuenta' : 'cambiar tu clave'}, ingresa el email
            registrado y crea una nueva contraseña.
          </Text>
          <Text>
            Por seguridad, tu clave debe tener al menos una <Strong>complejidad media</Strong>. Para esto, combina
            mayúsculas, minúsculas, números y símbolos, y asegúrate de que tenga 10 o más caracteres.
          </Text>
        </Flex>
        <Card asChild>
          <Form.Root onSubmit={handleSubmit}>
            <Flex direction="column" gap="4" p="2">
              <Form.Field name="email">
                <Form.Label>Email</Form.Label>
                <Form.Control asChild>
                  <TextField.Root
                    onChange={handleInputChange}
                    disabled={isSubmitting}
                    value={formData.email}
                    name="email"
                    type="email"
                    required
                  />
                </Form.Control>
                <Form.Message match="valueMissing">Por favor ingresa tu email.</Form.Message>
                <Form.Message match="typeMismatch">Por favor ingresa un email válido.</Form.Message>
              </Form.Field>

              <Form.Field name="password">
                <Form.Label>Nueva Clave</Form.Label>
                <Form.Control asChild>
                  <TextField.Root
                    onChange={handleInputChange}
                    value={formData.password}
                    disabled={isSubmitting}
                    name="password"
                    type="password"
                    required
                  />
                </Form.Control>

                <Flex align="center" justify="center" gap="2" mt="2">
                  {strength > 2 ? (
                    <CheckCircleIcon color={strengthColor[strength]} />
                  ) : strength > 1 ? (
                    <PlusCircleIcon color={strengthColor[strength]} />
                  ) : (
                    <CircleXIcon color={strengthColor[strength]} />
                  )}
                  <Progress value={(strength / 3) * 100} color={strengthColor[strength]} />
                </Flex>

                <Form.Message match={() => strength === 2}>La clave es suficiente pero podría ser mejor!</Form.Message>
                <Form.Message match={() => strength < 2}>La clave debe ser más fuerte.</Form.Message>
                <Form.Message match="valueMissing">Por favor ingresa una clave.</Form.Message>
              </Form.Field>

              <Form.Field name="confirmPassword">
                <Form.Label>Confirmar Clave</Form.Label>
                <Form.Control asChild>
                  <TextField.Root
                    value={formData.confirmPassword}
                    onChange={handleInputChange}
                    disabled={isSubmitting}
                    name="confirmPassword"
                    type="password"
                    required
                  />
                </Form.Control>
                <Form.Message match="valueMissing">Por favor confirma tu clave.</Form.Message>
                <Form.Message match={checkMatches}>Las claves deben ser iguales.</Form.Message>
              </Form.Field>

              {(errorUserActivateFirst || errorFetchToken) && (
                <Callout.Root color="gray" role="alert">
                  <Callout.Icon>
                    <CircleXIcon />
                  </Callout.Icon>
                  <Callout.Text>
                    {errorFetchToken?.message || errorUserActivateFirst?.message || 'Error al activar la cuenta.'}
                  </Callout.Text>
                </Callout.Root>
              )}

              <Button type="submit" disabled={isSubmitting} loading={isSubmitting}>
                <LockOpenIcon />
                <Text>Guardar</Text>
              </Button>

              <Button disabled={isSubmitting} variant="ghost" onClick={goToSignIn}>
                <Text>Cancelar</Text>
              </Button>
            </Flex>
          </Form.Root>
        </Card>
      </Grid>
    </Container>
  )
}

export default UserActivate
